import {
  Breadcrumb,
  Card,
  Col,
  Form,
  Input,
  message,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Upload,
} from "antd";
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getRequest, patchRequest, postRequest } from "../../../axios";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import InputField from "../../../common/htmlComponents/InputField";
import MaterialImageUpload from "./MaterialImageUpload";
import { ReactComponent as About } from "../../../image/About.svg";
import { ReactComponent as Name } from "../../../image/Name.svg";
import { ReactComponent as RupeesSign } from "../../../image/RupeesSign.svg";
import { ReactComponent as User } from "../../../image/User.svg";
import "../../../styles/Main.scss";
import Title from "antd/lib/skeleton/Title";

const { Option } = Select;

const AddMaterial = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const materialMasterId = id;
  const [state, setState] = useState({
    category_tree_name: null,
    category_tree: null,
    material_type: null,
    code: "",
    short_description: "",
    long_description: "",
    unit_of_measurement: null,
    estimated_price: "",
    image: null,
    pricing_type: "common",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [unitOfMeasurement, setUnitOfMeasurement] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [materialType, setMaterialType] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [uploadFileProfilePic, setUploadFileProfilePic] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [priceMode, setPriceMode] = useState("common");
  const [commonPrice, setCommonPrice] = useState("0");
  const [uniqueSizes, setUniqueSizes] = useState([]);
  const [steelPriceData, setSteelPriceData] = useState({
    sizes: [],
    prices: [],
  });
  const [allUnits, setAllUnits] = useState([]);

  const isSteelCategory =
    selectedCategory?.toLowerCase() === "structural steel" ||
    selectedCategory?.toLowerCase() === "reinforcement steel";

  const isCementCategory = selectedCategory?.toLowerCase() === "cement";

  useEffect(() => {
    if (!materialMasterId) {
      const getDiameter = async () => {
        try {
          const apiResponse = await getRequest("diameter/");
          if (apiResponse.data.success) {
            const sortedSizes = apiResponse.data.data.sort(
              (a, b) => a.ordering - b.ordering
            );
            setUniqueSizes(sortedSizes);
            setSteelPriceData({
              sizes: sortedSizes.map((item) => item.diameter_value),
              prices: sortedSizes.map(() => "0"),
            });
          }
        } catch (error) {
          ErrorNotificationMsg(t("failed_to_fetch_diameters"));
        }
      };
      getDiameter();
    }
  }, [t, materialMasterId]);
  
  const handlePriceChange = (index, value) => {
    if (/^\d*$/.test(value) || value === "") {
      const newPrices = [...steelPriceData.prices];
      newPrices[index] = value;
      setSteelPriceData((prev) => ({
        ...prev,
        prices: newPrices,
      }));

      // Update form field immediately
      const diameter = uniqueSizes.reduce((acc, sizeObj, idx) => {
        acc[sizeObj.diameter_value] =
          idx === index ? value : newPrices[idx] || "0";
        return acc;
      }, {});
      form.setFieldsValue({ diameter });
    }
  };

  const columns = [
    {
      title: t("Diameter Size") || "Diameter Size",
      dataIndex: "size",
      key: "size",
      render: (text, record) => {
        const formattedSize =
          record.size && record.size.endsWith(".00")
            ? record.size.replace(".00", "")
            : record.size || "";
        return <strong>{`${formattedSize} ${record.uom || "mm"}`}</strong>;
      },
    },
    {
      title: t("Price") || "Price (₹/kg)",
      dataIndex: "price",
      key: "price",
      render: (text, record, index) => (
        <Input
          type="number"
          value={steelPriceData.prices[index] || "0"}
          onChange={(e) => handlePriceChange(index, e.target.value)}
          placeholder={t("pl_estimated_price") || "Enter estimated price"}
          maxLength={15}
          className="TextFeild"
          style={{ width: "100px" }}
        />
      ),
    },
  ];

  const dataSource = uniqueSizes.map((sizeObj, index) => ({
    key: index,
    size: materialMasterId
      ? sizeObj?.diameter_value
        ? String(sizeObj.diameter_value)
        : ""
      : sizeObj?.value
      ? String(sizeObj.value)
      : "",
    uom: sizeObj?.diameter_uom || "mm",
    price: steelPriceData.prices[index] || "0",
  }));

  useEffect(() => {
    if (isSteelCategory && priceMode === "diameter" && uniqueSizes.length > 0) {
      const diameter = uniqueSizes.reduce((acc, sizeObj, index) => {
        acc[sizeObj.diameter_value] = String(
          steelPriceData.prices[index] || "0"
        );
        return acc;
      }, {});
      form.setFieldsValue({ diameter });
    } else {
      form.setFieldsValue({ diameter: {} });
    }
  }, [steelPriceData.prices, uniqueSizes, form, isSteelCategory, priceMode]);

  useEffect(() => {
    getCategoryTree();
  }, []);

  useEffect(() => {
    if (materialMasterId) {
      const materialGetById = async () => {
        setApiLoading(true);
        try {
          const response = await getRequest(
            `material-master/${materialMasterId}/`
          );
          
          if (response.data.success) {
            const data = response.data.data;
            setState({
              category_tree_name: data.category_tree_name,
              category_tree: data.category_tree,
              material_type: data.material_type,
              material_type_name: data.material_type_name,

              code: data.code || "",
              short_description: data.short_description || "",
              long_description: data.long_description || "",
              unit_of_measurement: data.unit_of_measurement,
              estimated_price: data.estimated_price
                ? String(data.estimated_price)
                : "0",
              image: data.image,
              pricing_type: data.pricing_type,
            });

            const fetchedPriceMode =
              data.pricing_type === "diameter_wise" ? "diameter" : "common";
            setPriceMode(fetchedPriceMode);
            setCommonPrice(
              data.estimated_price ? String(data.estimated_price) : "0"
            );

            if (data.diameter_prices && data.diameter_prices.length > 0) {
              const sortedDiameterPrices = data.diameter_prices.sort(
                (a, b) => a.ordering - b.ordering
              );
              setUniqueSizes(
                sortedDiameterPrices.map((item) => ({
                  id: item.diameter,
                  diameter_value: item.diameter_value,
                  value: item.diameter_value,
                  ordering: item.ordering,
                  estimated_price: item.estimated_price,
                  diameter_uom: item.diameter_uom || "mm",
                }))
              );
              setSteelPriceData({
                sizes: sortedDiameterPrices.map((item) => item.diameter_value),
                prices: sortedDiameterPrices.map((item) =>
                  String(item.estimated_price)
                ),
              });

              const diameter = sortedDiameterPrices.reduce((acc, item) => {
                acc[item.diameter_value] = String(item.estimated_price);
                return acc;
              }, {});
              setTimeout(() => {
                form.setFieldsValue({ diameter });
              }, 100);
            }

            if (data.image) {
              setFileList([
                { url: data.image, name: data.image.split("/").pop() },
              ]);
            }
            await getMaterialTypeByCatagory(data.category_tree);
            await getUnitOfMeasurement(data.category_tree);

            form.setFieldsValue({
              category_tree: data.category_tree,
              material_type: data.material_type_name,
              code: data.code,
              short_description: data.short_description,
              long_description: data.long_description,
              unit_of_measurement: data.unit_of_measurement,
              estimated_price: data.estimated_price
                ? String(data.estimated_price)
                : "0",
              pr_view: fetchedPriceMode,
            });
          } else {
            ErrorNotificationMsg(t("error_in_list_material"));
          }
        } catch (error) {
          ErrorNotificationMsg(t("failed_to_fetch_material"));
        } finally {
          setApiLoading(false);
        }
      };
      materialGetById();
    }
  }, [materialMasterId, t, form]);

  useEffect(() => {
    if (categoryTree.length && state.category_tree) {
      const selected = categoryTree.find(
        (item) => item.id === state.category_tree
      );
      setSelectedCategory(selected?.name);
    }
  }, [categoryTree, state.category_tree]);

  useEffect(() => {
    form.setFieldsValue({ pr_view: priceMode });
    if (isSteelCategory && priceMode === "diameter") {
      const diameter = uniqueSizes.reduce((acc, sizeObj, index) => {
        acc[sizeObj.diameter_value] = String(
          steelPriceData.prices[index] || "0"
        );
        return acc;
      }, {});
      form.setFieldsValue({ diameter });
    } else {
      form.setFieldsValue({ diameter: {} });
    }
  }, [priceMode, form, isSteelCategory, uniqueSizes, steelPriceData.prices]);

  const getCategoryTree = async () => {
    try {
      const response = await getRequest("category-tree/category-tree-list/?no_pagination=True");
      setCategoryTree(response.data.data || []);
    } catch (error) {
      ErrorNotificationMsg(t("failed_to_fetch_categories"));
    }
  };

  const getUnitOfMeasurement = async (categoryId) => {
    if (!categoryId) return; // Don't make API call if no category ID is provided
    try {
      const response = await getRequest(`get-category-by-uom/?category_tree=${categoryId}`);
      // setUnitOfMeasurement(response.data.results.data || []);
      setAllUnits(response.data.results.data || []);
    } catch (error) {
      ErrorNotificationMsg(t("failed_to_fetch_uom"));
    }
  };

  const getMaterialTypeByCatagory = async (id) => {
    try {
      const response = await getRequest(
        `material-type/?all_category_tree_id=${id}`
      );
      setMaterialType(response.data.results.data || []);
    } catch (error) {
      ErrorNotificationMsg(t("failed_to_fetch_material_types"));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const handleEstimatedInputChange = (value) => {
    if (/^\d*$/.test(value) || value === "") {
      setState((prev) => ({ ...prev, estimated_price: value }));
    }
  };

  const handleCommonPriceChange = (value) => {
    if (/^\d*$/.test(value) || value === "") {
      setCommonPrice(value);
    }
  };

  const handleSelectChange = (name, value) => {
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategorySelectChange = async (value) => {
    const selected = categoryTree.find((item) => item.id === value);
    setSelectedCategory(selected?.name);
    setState((prev) => ({
      ...prev,
      category_tree: value,
      category_tree_name: selected?.name,
      material_type: null,
      unit_of_measurement: null,
    }));
    form.setFieldsValue({ material_type: null, unit_of_measurement: null  });
    setMaterialType([]);
    await getMaterialTypeByCatagory(value);
    await getUnitOfMeasurement(value);
  };

  const handleUnitOfMeasurement = (value) => {
    setState((prev) => ({ ...prev, unit_of_measurement: value }));
  };

  const validateDiameterPrices = () => {
    if (isSteelCategory && priceMode === "diameter") {
      const diameter = form.getFieldValue("diameter") || {};
      const hasAtLeastOnePrice = Object.values(diameter).some(
        (price) => price !== "" && price !== null && price !== "0"
      );
      if (!hasAtLeastOnePrice) {
        throw new Error(
          t("val_at_least_one_diameter_price") ||
            "At least one diameter price must be provided"
        );
      }
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      validateDiameterPrices();
      setBtnLoading(true);

      let payload = null;

      if (state.category_tree_name === "Cement" || state.category_tree_name === "Sand" || state.category_tree_name === "Aggregate") {
        payload = {
          category_tree: state.category_tree,
          material_type: state.material_type,
          code: state.code,
          short_description: state.short_description,
          long_description: state.long_description,
          unit_of_measurement: state.unit_of_measurement,
          estimated_price:
            state.category_tree_name === "Cement" || state.category_tree_name === "Sand" || state.category_tree_name === "Aggregate"
              ? state.estimated_price
              : priceMode === "common"
              ? commonPrice
              : "0",
          pricing_type: "common",
        };
      } else {
        payload = {
          category_tree: state.category_tree,
          material_type: state.material_type,
          code: state.code,
          short_description: state.short_description,
          long_description: state.long_description,
          unit_of_measurement: state.unit_of_measurement,
          estimated_price:
            state.category_tree === 1
              ? state.estimated_price
              : priceMode === "common"
              ? commonPrice
              : "0",
          pricing_type: priceMode === "common" ? "common" : "diameter_wise",
          // diameter_prices: [],
        };
      }

      if (isSteelCategory && priceMode === "diameter") {
        const diameter = form.getFieldValue("diameter") || {};
        payload.diameter_prices = uniqueSizes
          .map((sizeObj, index) => {
            const price = materialMasterId
              ? diameter[sizeObj.diameter_value] ||
                steelPriceData.prices[index] ||
                "0"
              : steelPriceData.prices[index] || "0";
            if (price && price !== "0") {
              return {
                diameter_id: sizeObj.id,
                estimated_price: parseInt(price, 10),
              };
            }
            return null;
          })
          .filter((item) => item !== null);
      }

      const formData = new FormData();
      if (uploadFileProfilePic) {
        formData.append("image", uploadFileProfilePic);
      }
      formData.append("form_data", JSON.stringify(payload));

      let response;
      if (materialMasterId) {
        response = await patchRequest(
          `material-master/${materialMasterId}/`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      } else {
        response = await postRequest("material-master/", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            "ngrok-skip-browser-warning": "true",
          },
        });
      }

      if (response.data.success) {
        SuccessNotificationMsg(
          materialMasterId
            ? t("material_updated_successfully")
            : t("material_added_successfully")
        );
        form.resetFields();
        setState({
          category_tree: null,
          material_type: null,
          code: "",
          short_description: "",
          long_description: "",
          unit_of_measurement: null,
          estimated_price: "",
          image: null,
          pricing_type: "common",
        });
        setPriceMode("common");
        setCommonPrice("0");
        setSteelPriceData({
          sizes: uniqueSizes.map((item) => item.diameter_value),
          prices: uniqueSizes.map(() => "0"),
        });
        setFileList([]);
        setUploadFileProfilePic(null);
        setSelectedCategory(null);
        navigate("/material");
      } else {
        ErrorNotificationMsg(
          response.data.message || t("material_save_failed")
        );
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        t("material_save_failed");
      ErrorNotificationMsg(errorMessage);
    } finally {
      setBtnLoading(false);
    }
  };

  const backToList = () => {
    form.resetFields();
    setState({
      category_tree: null,
      material_type: null,
      code: "",
      short_description: "",
      long_description: "",
      unit_of_measurement: null,
      estimated_price: "",
      image: null,
      pricing_type: "common",
    });
    setPriceMode("common");
    setCommonPrice("0");
    setSteelPriceData({
      sizes: uniqueSizes.map((item) => item.diameter_value),
      prices: uniqueSizes.map(() => "0"),
    });
    setFileList([]);
    setUploadFileProfilePic(null);
    setSelectedCategory(null);
    navigate("/material");
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG file!");
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  useEffect(() => {
    if (isSteelCategory) {
      const filteredUnits = allUnits.filter((item) =>
        ["MT", "KG"].includes(item.unit_of_measurement)
      );
      setUnitOfMeasurement(filteredUnits);
      const currentUOM = form.getFieldValue("unit_of_measurement");
      if (currentUOM && !filteredUnits.some((item) => item.id === currentUOM)) {
        form.setFieldsValue({ unit_of_measurement: null });
        setState((prev) => ({ ...prev, unit_of_measurement: null }));
      }
    } else if (isCementCategory) {
      const filteredUnits = allUnits.filter(
        (i) => i.unit_of_measurement !== "KG"
      );
      setUnitOfMeasurement(filteredUnits);
    } else {
      setUnitOfMeasurement(allUnits);
    }
  }, [isSteelCategory, allUnits, form, isCementCategory]);

  

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip placement="right" title={t("material_form_tooltip")}>
              <h3>
                {materialMasterId ? t("Edit Material") : t("Add Material")}
              </h3>
            </Tooltip>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/material">{t("material master")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                {materialMasterId ? t("Edit Material") : t("Add Material")}
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
      </div>

      <section className="grid-sec FormSection Container">
        <div className="FormBody">
          <Spin spinning={apiLoading}>
            <Form
              form={form}
              className="ModalForm"
              onFinish={handleSubmit}
              autoComplete="off"
              layout="vertical"
            >
              <Row gutter={[15]}>
                <Col md={24} className="TextFeild">
                  <MaterialImageUpload
                    beforeUpload={beforeUpload}
                    deleteFlag={0}
                    setDeleteFlag={() => {}}
                    setUploadFileProfilePic={setUploadFileProfilePic}
                    fileList={fileList}
                    setFileList={setFileList}
                    identifyProfileUrl={state.image}
                    setIdentifyProfileUrl={(url) =>
                      setState((prev) => ({ ...prev, image: url }))
                    }
                    materialMasterId={materialMasterId}
                  />
                </Col>
                <Col md={8} className="TextFeild">
                  <Form.Item
                    name="category_tree"
                    label={t("label_category_tree")}
                    rules={[
                      {
                        required: true,
                        message: <Trans>{t("val_category_tree")}</Trans>,
                      },
                    ]}
                  >
                    <Select
                      placeholder={t("pl_category_tree")}
                      optionFilterProp="children"
                      onChange={handleCategorySelectChange}
                      aria-label={t("label_category_tree")}
                    >
                      {categoryTree.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col md={8} className="TextFeild">
                  <Form.Item
                    name="material_type"
                    label={t("pl_material_type4")}
                    rules={[
                      {
                        required: true,
                        message: <Trans>{t("val_select_material_type")}</Trans>,
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder={t("pl_select_material_type")}
                      optionFilterProp="children"
                      onChange={(value) =>
                        handleSelectChange("material_type", value)
                      }
                      aria-label={t("pl_material_type4")}
                      disabled={state.category_tree === null}
                    >
                      {materialType
                        ?.filter((i) => i.is_selected !== true)
                        ?.map((item) => {
                          return (
                          <Option key={item.id} value={item.id}>
                            {item.type_name}
                          </Option>
                        )})}
                    </Select>
                  </Form.Item>
                </Col>
                <Col md={8} className="TextFeild">
                  <InputField
                    name="short_description"
                    label={t("label_short_description")}
                    placeholder={t("pl_short_description")}
                    rules={t("val_short_description")}
                    
                    prefix={<About />}
                    handleInputChange={handleInputChange}
                    maxLength={140}
                  />
                </Col>
                <Col md={8} className="TextFeild">
                  <InputField
                    name="long_description"
                    label={t("label_long_description")}
                    placeholder={t("pl_long_description")}
                    rules={t("val_long_description")}
                    prefix={<About />}
                    handleInputChange={handleInputChange}
                    maxLength={140}
                  />
                </Col>
                <Col md={8} className="TextFeild">
                  <InputField
                    name="code"
                    label={t("label_code")}
                    placeholder={t("pl_code")}
                    rules={t("val_code")}
                    
                    prefix={<Name />}
                    handleInputChange={handleInputChange}
                  />
                </Col>
                {selectedCategory &&
                  selectedCategory.toLowerCase() !== "structural steel" &&
                  selectedCategory.toLowerCase() !== "reinforcement steel" && (
                    <Col md={8} className="TextFeild">
                      <Form.Item
                        name="estimated_price"
                        label={t("label_estimated_price")}
                        rules={[
                          {
                            required: true,
                            pattern: /^\d*$/,
                            message: <Trans>{t("val_estimated_price1")}</Trans>,
                          },
                        ]}
                      >
                        <Input
                          placeholder="0"
                          type="number"
                          value={state.estimated_price}
                          prefix={<RupeesSign />}
                          onChange={(e) =>
                            handleEstimatedInputChange(e.target.value)
                          }
                          maxLength={15}
                          aria-label={t("label_estimated_price")}
                        />
                      </Form.Item>
                    </Col>
                  )}
                <Col md={8} className="TextFeild">
                  <Form.Item
                    name="unit_of_measurement"
                    label={t("label_unit_of_measurement")}
                    rules={[
                      { required: true, message: <Trans>{t("val_unit_of_measurement")}</Trans> },
                    ]}
                  >
                    <Select
                      // showSearch
                      placeholder={t("pl_unit_of_measurement")}
                      optionFilterProp="children"
                      onChange={handleUnitOfMeasurement}
                      aria-label={t("label_unit_of_measurement")}
                      disabled={state.category_tree === null}
                    >
                      {unitOfMeasurement.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.unit_of_measurement}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                {isSteelCategory && (
                  <div style={{ marginTop: "15px" }}>
                    <Col
                      xs={24}
                      sm={24}
                      md={24}
                      lg={24}
                      xl={24}
                      className="TextFeild gutter-row"
                    >
                      <Space align="center" size={[8, 16]} wrap>
                        <Form.Item
                          className="radioitem"
                          name="pr_view"
                          initialValue={priceMode}
                          rules={[
                            {
                              required: true,
                              message:
                                <Trans>{t("val_select_price_mode") ||
                                "Please select a price mode"}</Trans>,
                            },
                          ]}
                        >
                          <Radio.Group
                            value={priceMode}
                            onChange={(e) => setPriceMode(e.target.value)}
                            className="prviewlabel radio"
                          >
                            <Radio value="common">Common Estimated Price</Radio>
                            <Radio value="diameter">Diameter Wise Price</Radio>
                          </Radio.Group>
                        </Form.Item>

                        <Form.Item name="diameter" hidden>
                          <Input hidden />
                        </Form.Item>
                      </Space>
                    </Col>
                    {priceMode === "common" ? (
                      <Col md={10} className="TextFeild">
                        <Form.Item
                          name="estimated_price"
                          label={t("label_estimated_price")}
                          rules={[
                          {
                            required: true,
                            message: <Trans>{t("val_estimated_price1")}</Trans>,
                          },
                        ]}
                        >
                          <Input
                            type="number"
                            value={commonPrice}
                            onChange={(e) =>
                              handleCommonPriceChange(e.target.value)
                            }
                            placeholder={
                              t("pl_estimated_price") || "Enter common price"
                            }
                            maxLength={15}
                            prefix={<RupeesSign />}
                            className="TextFeild"
                          />
                        </Form.Item>
                      </Col>
                    ) : (
                      <Table
                        columns={columns}
                        dataSource={dataSource}
                        pagination={false}
                        scroll={{ x: true }}
                        style={{ marginTop: 16, width: "100%" }}
                      />
                    )}
                  </div>
                )}
              </Row>
              <div className="ModalPopupFooter">
                <ButtonField
                  type="primary"
                  className="CancelGradiantBtn"
                  htmlType="button"
                  label={t("cancel")}
                  onClick={backToList}
                />
                <ButtonField
                  type="primary"
                  className="SaveGradiantBtn"
                  htmlType="submit"
                  label={t("save")}
                  loading={btnLoading}
                  disabled={apiLoading}
                />
              </div>
            </Form>
          </Spin>
        </div>
      </section>
    </>
  );
};

export default AddMaterial;
